import { io, type Socket } from 'socket.io-client';
import { env } from '@/lib/env';
import type {
  PresenceStatus,
  MessageCreatePayload,
  MessageUpdatePayload,
  MessageDeletePayload,
  ReactionUpdatePayload,
  PresenceUpdatePayload,
  TypingPayload,
  SubscribedPayload,
  VoiceJoinPayload,
  VoiceLeavePayload,
  VoiceStateUpdatePayload,
  NotificationCreatePayload,
  UnreadUpdatePayload,
  ServerBanPayload,
  MemberTimeoutPayload,
  ProfileUpdatePayload,
  StreamCreatedPayload,
  StreamUpdatedPayload,
  StreamEndedPayload,
  StreamViewerCountPayload,
} from '@disenchantment/types';

export interface ServerStructureUpdatePayload {
  serverId: string;
  event: string;
  categoryId?: string;
  channelId?: string;
}

/** Emitted when a new audit log row is created (roles, bans, message deletes, etc.). */
export interface AdminUpdatePayload {
  serverId: string;
  action: string;
  targetId?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  log?: unknown;
}

export interface SocketServiceEvents {
  connect: () => void;
  disconnect: (reason: string) => void;
  message_create: (payload: MessageCreatePayload) => void;
  message_update: (payload: MessageUpdatePayload) => void;
  message_delete: (payload: MessageDeletePayload) => void;
  reaction_update: (payload: ReactionUpdatePayload) => void;
  presence_update: (payload: PresenceUpdatePayload) => void;
  typing_start: (payload: TypingPayload) => void;
  typing_stop: (payload: Omit<TypingPayload, 'timestamp'>) => void;
  subscribed: (payload: SubscribedPayload) => void;
  subscribed_server: (payload: { servers: string[] }) => void;
  presence_snapshot: (snapshot: Record<string, { status: PresenceStatus; lastSeenAt: string }>) => void;
  server_structure_update: (payload: ServerStructureUpdatePayload) => void;
  admin_update: (payload: AdminUpdatePayload) => void;
  profile_update: (payload: ProfileUpdatePayload) => void;
  STREAM_CREATED: (payload: StreamCreatedPayload) => void;
  STREAM_UPDATED: (payload: StreamUpdatedPayload) => void;
  STREAM_ENDED: (payload: StreamEndedPayload) => void;
  STREAM_VIEWER_COUNT: (payload: StreamViewerCountPayload) => void;
  stream_error: (payload: { event: string; message: string }) => void;
  VOICE_JOIN: (payload: VoiceJoinPayload) => void;
  VOICE_LEAVE: (payload: VoiceLeavePayload) => void;
  VOICE_STATE_UPDATE: (payload: VoiceStateUpdatePayload) => void;
  notification_create: (payload: NotificationCreatePayload) => void;
  unread_update: (payload: UnreadUpdatePayload) => void;
  server_ban: (payload: ServerBanPayload) => void;
  member_timeout: (payload: MemberTimeoutPayload) => void;
}

class SocketService {
  private socket: Socket | null = null;

  /**
   * Channel IDs this client has subscribed to.
   * Maintained across reconnects — on every `connect` event the full set is
   * re-emitted to the gateway so rooms are automatically restored after a
   * disconnect/reconnect cycle.
   */
  private activeChannels = new Set<string>();
  private activeServers = new Set<string>();

  private _lastConnectedAt: number | null = null;

  /**
   * Connect and authenticate with the gateway.
   *
   * Guard: `if (this.socket) return` — not `if (this.socket?.connected)`.
   * While connecting, `connected === false`. The old guard created a new io()
   * instance during the connecting window, discarding the in-flight socket and
   * producing the connection storm.
   */
  connect(token: string): void {
    if (this.socket) return;

    console.log('[socket] connecting to', env.VITE_GATEWAY_URL);

    this.socket = io(env.VITE_GATEWAY_URL, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30_000,
    });

    this.socket.on('connect', () => {
      this._lastConnectedAt = Date.now();
      console.log('[socket] connected', { id: this.socket?.id });

      // Resubscribe to all tracked channels on every connect event.
      // This handles both initial connection and reconnection after a drop.
      if (this.activeChannels.size > 0) {
        const channels = [...this.activeChannels];
        console.log('[socket] resubscribing after connect', { channels });
        this.socket?.emit('subscribe', channels);
      }

      if (this.activeServers.size > 0) {
        const servers = [...this.activeServers];
        console.log('[socket] resubscribing server rooms after connect', { servers });
        this.socket?.emit('subscribe_server', servers);
      }

      // Refresh presence snapshot so online/offline indicators are accurate
      // after a reconnect (other users may have changed status while offline).
      this.socket?.emit('get_presence');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[socket] disconnected', { reason });
    });

    this.socket.on('connect_error', (err) => {
      console.warn('[socket] connect_error', { message: err.message });
    });
  }

  /** Disconnect and clean up. Called on logout. */
  disconnect(): void {
    console.log('[socket] disconnecting');
    this.activeChannels.clear();
    this.activeServers.clear();
    this.socket?.disconnect();
    this.socket = null;
  }

  /**
   * Update the auth token used for reconnection.
   * Called when Supabase refreshes the JWT (TOKEN_REFRESHED event).
   */
  updateToken(token: string): void {
    if (this.socket) {
      (this.socket as Socket & { auth: { token: string } }).auth = { token };
    }
  }

  /**
   * Subscribe to channel rooms on the gateway.
   * Records the channel IDs so they can be resubscribed after reconnect.
   */
  subscribe(channelIds: string[]): void {
    if (!channelIds.length) return;
    channelIds.forEach((id) => this.activeChannels.add(id));
    console.log('[socket] subscribe', { channelIds, total: this.activeChannels.size });
    this.socket?.emit('subscribe', channelIds);
  }

  /**
   * Unsubscribe from channel rooms on the gateway.
   * Removes the channel IDs from the tracked set.
   */
  unsubscribe(channelIds: string[]): void {
    if (!channelIds.length) return;
    channelIds.forEach((id) => this.activeChannels.delete(id));
    console.log('[socket] unsubscribe', { channelIds, remaining: this.activeChannels.size });
    this.socket?.emit('unsubscribe', channelIds);
  }

  subscribeServer(serverIds: string[]): void {
    if (!serverIds.length) return;
    serverIds.forEach((id) => this.activeServers.add(id));
    this.socket?.emit('subscribe_server', serverIds);
  }

  unsubscribeServer(serverIds: string[]): void {
    if (!serverIds.length) return;
    serverIds.forEach((id) => this.activeServers.delete(id));
    this.socket?.emit('unsubscribe_server', serverIds);
  }

  /** Request a full presence snapshot from the gateway. */
  requestPresence(): void {
    this.socket?.emit('get_presence');
  }

  sendTypingStart(channelId: string): void {
    this.socket?.emit('typing_start', channelId);
  }

  sendTypingStop(channelId: string): void {
    this.socket?.emit('typing_stop', channelId);
  }

  setStatus(status: PresenceStatus): void {
    this.socket?.emit('set_status', status);
  }

  /**
   * Register a typed event listener. Returns a cleanup function.
   *
   * Captures the current socket INSTANCE at registration time so that cleanup
   * always removes from the correct socket, even if the instance is later replaced.
   */
  on<K extends keyof SocketServiceEvents>(
    event: K,
    handler: SocketServiceEvents[K]
  ): () => void {
    const target = this.socket;
    target?.on(event as string, handler as (...args: unknown[]) => void);
    return () => target?.off(event as string, handler as (...args: unknown[]) => void);
  }

  // ── Connection state ──────────────────────────────────────────────────────

  /** True when the WebSocket handshake has completed. */
  get isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /** True while the socket object exists but has not yet completed the handshake. */
  get isConnecting(): boolean {
    return this.socket !== null && !(this.socket.connected);
  }

  /** Unix timestamp (ms) of the most recent successful connection. Null before first connect. */
  get lastConnectedAt(): number | null {
    return this._lastConnectedAt;
  }
}

// Singleton — one connection per browser tab
export const socketService = new SocketService();
